/*
 * The history import, said in one line above the list (step 10, Q3; Dawid's answer of 2026-10-18 in
 * docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md, "Step 10"): while a campaign's history is
 * importing, the list is incomplete, and this says why, for a person with the reply agent, the only
 * people who onboard a campaign. The words and the numbers are the old inbox's banner's
 * (`src/backfill-progress.ts`): "Drafting history", the campaign, how many wait and at what pace, and
 * a thin bar. The line itself is no live region; a polite one beside it speaks only at a quarter
 * passed and at the end, so a minute's poll never talks over the rep. A load that arrives mid-import
 * announces nothing.
 */
import { useEffect, useRef, useState } from "react";
import {
  type BackfillCampaign,
  backfillCountText,
  backfillMilestone,
  importingCampaigns,
  summarizeBackfill,
} from "../../src/backfill-progress.ts";
import { CheckIcon } from "../icons.tsx";

/** The old banner's ring, drawn with a gap so it reads as work in progress while still. */
export function ImportRing() {
  return (
    <svg
      className="lcr-progress-arc"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.55" />
    </svg>
  );
}

/** What the polite region says at a quarter passed and at the end; the old banner's words. */
function milestoneAnnouncement(milestone: 25 | 50 | 75): string {
  return `Drafting history: ${milestone}% done.`;
}
const FINISHED_ANNOUNCEMENT = "History drafting complete. Threads are editable.";

/** How long the line says the import finished before it goes. */
const FINISHED_MS = 4_000;

/**
 * What to say once, politely: a quarter passed, or the import finished, from one read to the next.
 * The campaign that finished is kept for the line's last words.
 */
function useImportMoments(campaigns: readonly BackfillCampaign[]) {
  const [finished, setFinished] = useState<{ name: string | null } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const previous = useRef<{ names: Map<string, string>; milestone: number } | null>(null);
  useEffect(() => {
    const active = importingCampaigns(campaigns);
    const names = new Map(active.map((campaign) => [campaign.campaignId, campaign.campaignName]));
    const milestone = backfillMilestone(summarizeBackfill(campaigns)?.percent ?? 0);
    const before = previous.current;
    previous.current = { names, milestone };
    if (!before) return;
    const completed = [...before.names].filter(([id]) => !names.has(id));
    if (completed.length > 0) {
      setFinished({ name: completed.length === 1 ? (completed[0]?.[1] ?? null) : null });
      setAnnouncement(FINISHED_ANNOUNCEMENT);
    } else if (milestone > before.milestone && milestone !== 0)
      setAnnouncement(milestoneAnnouncement(milestone));
  }, [campaigns]);
  useEffect(() => {
    if (!finished) return;
    const timer = setTimeout(() => setFinished(null), FINISHED_MS);
    return () => clearTimeout(timer);
  }, [finished]);
  useEffect(() => {
    if (!announcement) return;
    const timer = setTimeout(() => setAnnouncement(""), 6_000);
    return () => clearTimeout(timer);
  }, [announcement]);
  return { finished, announcement };
}

export function ImportNote(props: { campaigns: readonly BackfillCampaign[] }) {
  const summary = summarizeBackfill(props.campaigns);
  const { finished, announcement } = useImportMoments(props.campaigns);
  return (
    <>
      {summary ? (
        <div className="lcr-import-note" data-state="importing">
          <ImportRing />
          <strong className="lcr-import-note-label">Drafting history</strong>
          <span className="lcr-import-note-name" title={summary.name ?? undefined}>
            {summary.name ?? `${summary.campaigns} campaigns`}
          </span>
          <span className="lcr-import-note-count">{backfillCountText(summary)}</span>
          <span className="lcr-import-note-bar" aria-hidden="true">
            <span style={{ width: `${Math.max(summary.percent, 4)}%` }} />
          </span>
        </div>
      ) : finished ? (
        <div className="lcr-import-note" data-state="done">
          <CheckIcon size={14} />
          <strong className="lcr-import-note-label">History drafting complete</strong>
          {finished.name && (
            <span className="lcr-import-note-name" title={finished.name}>
              {finished.name}
            </span>
          )}
        </div>
      ) : null}
      <p className="lcr-visually-hidden" role="status">
        {announcement}
      </p>
    </>
  );
}
